import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import { getAccessState, type AccessState } from "./lib/billing";
import { safeReturnPath } from "./lib/authRedirect";
import { registerPushListeners } from "./lib/pushNotifications";
import { recordSessionStart } from "./lib/usageTracking";
import { checkAppVersion } from "./lib/appVersion";
import { useT } from "./lib/i18n";
import { LandingPage } from "./features/landing/LandingPage";
import { LoginPage } from "./features/auth/LoginPage";
import { SignUpPage } from "./features/auth/SignUpPage";
import { ForgotPasswordPage } from "./features/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "./features/auth/ResetPasswordPage";
import { OnboardingBusinessPage } from "./features/auth/OnboardingBusinessPage";
// Eager, not lazy (backlog PRINT-26): the one exception to the lazy-loading
// rule below. This route is never opened by a real user — only by
// Cloudflare's Browser Run service generating a PDF, which has no concept
// of "wait for this lazy chunk to download, then wait for Suspense to
// resolve" the way a real browser session does. A real Cloudflare test
// against this route while it WAS lazy-loaded captured the top-level
// Suspense fallback ("Loading…") as the entire PDF — Cloudflare's page-
// ready heuristic isn't sophisticated enough to wait through a code-split
// boundary on top of this page's own data fetch. Eager loading removes
// that race entirely rather than trying to out-wait it.
import { PrintDocumentPage } from "./features/quotes/PrintDocumentPage";
// Eager, same reasoning as PrintDocumentPage above: this is the one route
// that must be able to render before the top-level Suspense boundary even
// mounts (backlog MOBILE-16) — a lazy import here would mean a version-
// blocked user briefly sees the Suspense fallback, then this, instead of
// this immediately. Small component; negligible bundle cost either way.
import { UpdateRequiredPage } from "./features/app/UpdateRequiredPage";

// Lazy (backlog LAUNCH-10): everything behind the auth/paywall gate, plus the
// legal pages, is its own chunk loaded on demand — the main bundle was 711 KB
// uncompressed (~202 KB gzipped, over Vite's 500 KB warning threshold) with
// every feature bundled in regardless of which single page a session
// actually opens. The auth pages (Login/SignUp/Forgot/Reset) stay eager —
// every signed-out visitor needs one of them immediately, so lazy-loading
// them would only add a loading flicker with no real first-load benefit.
const DashboardPage = lazy(() => import("./features/app/DashboardPage").then((m) => ({ default: m.DashboardPage })));
const NewDocumentPage = lazy(() =>
  import("./features/app/NewDocumentPage").then((m) => ({ default: m.NewDocumentPage })),
);
// No sidebar nav entry, deliberately (backlog DOCS-21) — reachable via the
// "Manage templates" link on New Document, this page's own breadcrumb, and
// the "Manage" action on the "Template saved" toast.
const TemplatesPage = lazy(() =>
  import("./features/app/TemplatesPage").then((m) => ({ default: m.TemplatesPage })),
);
const HelpPage = lazy(() => import("./features/app/HelpPage").then((m) => ({ default: m.HelpPage })));
const QuoteBuilderPage = lazy(() =>
  import("./features/quotes/QuoteBuilderPage").then((m) => ({ default: m.QuoteBuilderPage })),
);
const InvoiceBuilderPage = lazy(() =>
  import("./features/quotes/InvoiceBuilderPage").then((m) => ({ default: m.InvoiceBuilderPage })),
);
const DocumentsPage = lazy(() =>
  import("./features/quotes/DocumentsPage").then((m) => ({ default: m.DocumentsPage })),
);
const DocumentViewPage = lazy(() =>
  import("./features/quotes/DocumentViewPage").then((m) => ({ default: m.DocumentViewPage })),
);
const G702BuilderPage = lazy(() =>
  import("./features/g702/G702BuilderPage").then((m) => ({ default: m.G702BuilderPage })),
);
const ClientsPage = lazy(() => import("./features/clients/ClientsPage").then((m) => ({ default: m.ClientsPage })));
const ClientFormPage = lazy(() =>
  import("./features/clients/ClientFormPage").then((m) => ({ default: m.ClientFormPage })),
);
const ClientDetailPage = lazy(() =>
  import("./features/clients/ClientDetailPage").then((m) => ({ default: m.ClientDetailPage })),
);
const ContractsPage = lazy(() =>
  import("./features/contracts/ContractsPage").then((m) => ({ default: m.ContractsPage })),
);
const ContractFormPage = lazy(() =>
  import("./features/contracts/ContractFormPage").then((m) => ({ default: m.ContractFormPage })),
);
const ContractDetailPage = lazy(() =>
  import("./features/contracts/ContractDetailPage").then((m) => ({ default: m.ContractDetailPage })),
);
const CatalogPage = lazy(() => import("./features/catalog/CatalogPage").then((m) => ({ default: m.CatalogPage })));
const ReportsPage = lazy(() => import("./features/reports/ReportsPage").then((m) => ({ default: m.ReportsPage })));
const AccountPage = lazy(() => import("./features/account/AccountPage").then((m) => ({ default: m.AccountPage })));
const Paywall = lazy(() => import("./features/billing/Paywall").then((m) => ({ default: m.Paywall })));
const LegalPage = lazy(() => import("./features/legal/LegalPage").then((m) => ({ default: m.LegalPage })));

// The internal ops console (revenue figures, charts) has no reason to ship
// in every trial customer's initial bundle either.
const AdminPage = lazy(() => import("./features/admin/AdminPage").then((m) => ({ default: m.AdminPage })));

/**
 * Shown instead of a builder/form route during the read-only grace period
 * (backlog LAUNCH-9) — client-side only, same as the rest of this gate; RLS
 * itself doesn't know about billing tiers (see App.tsx's own requireAccess
 * comment). Deliberately minimal: this isn't a dead end, just a redirect
 * target for the handful of routes that can't render read-only.
 */
function GraceOnlyNotice() {
  const t = useT();
  return (
    <div className="iv-loading flex-col gap-3 text-center">
      <p>{t("grace.notice.title")}</p>
      <p>
        <Link to="/documents" className="underline">
          {t("grace.notice.backLink")}
        </Link>
      </p>
    </div>
  );
}

/**
 * App shell + auth gate.
 *
 * Blueprint Night (Lovable-code retrofit) is now the canonical design for
 * every screen in the app — the page-by-page migration is complete.
 *
 * One top-level `<Suspense>` wraps every route (backlog LAUNCH-10) rather
 * than a per-route boundary — simpler, and no route here needs a distinct
 * fallback from any other. AdminPage's own previously-separate inner
 * Suspense was folded into this one.
 */
export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState<AccessState | null>(null);
  const [needsBusinessName, setNeedsBusinessName] = useState<boolean | null>(null);
  const [versionBlock, setVersionBlock] = useState<{ message: string | null } | null>(null);

  // Hard version floor (backlog MOBILE-16) — deliberately independent of
  // session/access state below: this has to be able to block even the
  // login screen, not just authenticated routes. No-op on web and on any
  // network/query failure (checkAppVersion() itself fails open) — this is
  // a nudge mechanism, never something that should be able to strand a
  // user over a transient error. Not gated on "still checking" the way
  // needsBusinessName is below: the common case (no block) shouldn't add
  // a loading delay to every app start for a check that almost never
  // fires.
  useEffect(() => {
    checkAppVersion().then((status) => {
      if (status.kind === "required") setVersionBlock({ message: status.message });
    });
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Passive usage capture (backlog ADMIN-1) — fires once per browser tab per
  // signed-in user (see recordSessionStart's sessionStorage guard), covering
  // every authenticated route uniformly.
  useEffect(() => {
    if (session) recordSessionStart(session.user.id);
  }, [session]);

  // Access gate: trial or active subscription. Runs whenever a session
  // appears. If returning from Stripe Checkout, poll briefly because the
  // webhook that flips the subscription to active can lag a second or two.
  //
  // Awaits accept_pending_invite() first (backlog AUTH-15) — this effect
  // and identity.tsx's IdentityProvider both react to the same auth event
  // independently, with no ordering guarantee between them. Deciding
  // access (and, below, the business-name onboarding gate) off an account
  // fetch that races ahead of invite acceptance meant a freshly-invited
  // teammate could still see the just-auto-created personal account
  // (always business_name-less, on its own fresh trial clock) rather than
  // the real team account they just joined. accept_pending_invite() is
  // idempotent and a same-transaction no-op when there's no matching
  // invite (0027_pending_invites.sql), so calling it here too (identity.tsx
  // already does, for its own unrelated reason) just removes the race by
  // construction instead of relying on timing.
  useEffect(() => {
    if (!session) {
      setAccess(null);
      return;
    }
    let cancelled = false;
    const checkout = new URLSearchParams(window.location.search).get("checkout");

    (async () => {
      await Promise.resolve(supabase.rpc("accept_pending_invite")).catch(() => {});
      if (checkout === "success") {
        for (let i = 0; i < 5 && !cancelled; i++) {
          const st = await getAccessState();
          if (st.reason === "subscription") break;
          await new Promise((r) => setTimeout(r, 1500));
        }
      }
      if (checkout) window.history.replaceState({}, "", window.location.pathname);
      const st = await getAccessState();
      if (!cancelled) setAccess(st);
    })().catch(() => {
      if (!cancelled)
        setAccess({
          hasAccess: false,
          reason: "none",
          tier: "locked",
          trialDaysLeft: 0,
          graceDaysLeft: null,
          subscription: null,
          account: null,
        });
    });

    return () => {
      cancelled = true;
    };
  }, [session]);

  // Onboarding gate (backlog AUTH-8): a Google/Microsoft signup has no
  // business_name yet (see OnboardingBusinessPage's own comment) — derived
  // from the account the access-gate effect above already fetched, not a
  // second getAccount() call on every authenticated page load (backlog
  // PERF-8, the same shape PERF-2 already fixed elsewhere; AccessState's
  // own comment covers why `account` can be trusted here as effectively
  // "fetch failed", not a real no-account state). `access === null` while
  // still loading leaves `needsBusinessName` at its own initial `null` too,
  // preserving requireAccess()'s three-state loading/true/false gate. A
  // fetch failure fails OPEN (assume no onboarding needed) rather than
  // trapping a signed-in user in a loading loop over a transient network
  // error — matching this effect's own pre-existing behavior.
  useEffect(() => {
    if (!session) {
      setNeedsBusinessName(null);
      return;
    }
    if (access === null) return;
    // access.account is null exactly on a fetch failure (see AccessState's
    // own comment) — !access.account?.business_name would evaluate that
    // as truthy (optional-chaining a null gives undefined, and !undefined
    // is true), forcing onboarding instead of failing open. Checked
    // explicitly instead.
    setNeedsBusinessName(access.account ? !access.account.business_name : false);
  }, [session, access]);

  // Restores the page someone was actually trying to reach (e.g. a document
  // link from a notification email) after signing in via OAuth — the one
  // path requireAccess()'s own `state.from` (below) can't carry, since
  // signInWithOAuth() does a full external redirect that drops React
  // Router state. LoginPage.tsx encodes it as `?returnTo=` on the OAuth
  // `redirectTo` URL instead; this is where it's consumed, once, after a
  // session actually exists.
  useEffect(() => {
    if (!session) return;
    const returnTo = safeReturnPath(new URLSearchParams(window.location.search).get("returnTo"));
    if (returnTo) {
      window.history.replaceState({}, "", window.location.pathname);
      navigate(returnTo, { replace: true });
    }
  }, [session, navigate]);

  // Registered once for the app's lifetime, independent of session state —
  // a tap can arrive while backgrounded and resume with a session already
  // restored by the time this fires. registerPushListeners() itself is a
  // no-op on web (build brief: native-only feature). safeReturnPath() guards
  // deepLink the same way it guards state.from/returnTo above — this value
  // arrives from a push payload this app's own send-notification built, but
  // validating it costs nothing.
  useEffect(() => {
    return registerPushListeners((deepLink) => {
      const target = safeReturnPath(deepLink);
      if (target) navigate(target);
    });
  }, [navigate]);

  if (versionBlock) return <UpdateRequiredPage message={versionBlock.message} />;
  if (loading) return <div className="iv-loading">Loading…</div>;

  // opts.allowGrace marks the routes that stay reachable during the
  // read-only grace period (backlog LAUNCH-9) — view/list pages where
  // nothing new gets created. Everything else (builder/form routes)
  // redirects to GraceOnlyNotice instead of rendering during grace; the
  // terminal "locked" tier (past grace, or a trial that expired with no
  // subscription ever) is unchanged from before this feature existed.
  function requireAccess(element: ReactNode, opts?: { allowGrace?: boolean }) {
    if (!session) return <Navigate to="/login" state={{ from: `${location.pathname}${location.search}` }} replace />;
    if (needsBusinessName === null) return <div className="iv-loading">Loading…</div>;
    if (needsBusinessName) return <Navigate to="/onboarding/business" replace />;
    if (access === null) return <div className="iv-loading">Loading…</div>;
    if (access.tier === "locked") return <Paywall email={session.user.email ?? ""} subscription={access.subscription} />;
    if (access.tier === "grace" && !opts?.allowGrace) return <GraceOnlyNotice />;
    return element;
  }

  return (
    <Suspense fallback={<div className="iv-loading">Loading…</div>}>
      <Routes>
        {/* Public legal pages — reachable signed in or out. */}
        <Route path="/terms" element={<LegalPage doc="terms" />} />
        <Route path="/privacy" element={<LegalPage doc="privacy" />} />
        <Route path="/support" element={<LegalPage doc="support" />} />
        {/* Reset-password link lands here regardless of session state. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Print-only route (backlog PRINT-26) — deliberately OUTSIDE
            requireAccess(): this has no user session at all (Cloudflare's
            Browser Run service navigates here directly), so there's no
            session for requireAccess() to gate on in the first place. Its
            only gate is the short-lived signed token in the query string,
            verified server-side by print-doc-data — see
            PrintDocumentPage's own comment. */}
        <Route path="/print/documents/:docKey" element={<PrintDocumentPage />} />

        <Route path="/login" element={session ? <Navigate to="/" replace /> : <LoginPage />} />
        <Route path="/signup" element={session ? <Navigate to="/" replace /> : <SignUpPage />} />
        <Route
          path="/forgot-password"
          element={session ? <Navigate to="/" replace /> : <ForgotPasswordPage />}
        />
        <Route
          path="/onboarding/business"
          element={
            !session ? (
              <Navigate to="/login" replace />
            ) : needsBusinessName === null ? (
              <div className="iv-loading">Loading…</div>
            ) : needsBusinessName ? (
              <OnboardingBusinessPage onSaved={() => setNeedsBusinessName(false)} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        {/* Signed out: the public marketing page (backlog LAUNCH-3), never
            the login redirect requireAccess() would otherwise give every
            other gated route. Signed in: unchanged Dashboard/paywall/
            loading behavior. */}
        <Route path="/" element={session ? requireAccess(<DashboardPage />, { allowGrace: true }) : <LandingPage />} />
        <Route path="/documents/new" element={requireAccess(<NewDocumentPage />)} />
        <Route path="/templates" element={requireAccess(<TemplatesPage />)} />
        <Route path="/documents/quote" element={requireAccess(<QuoteBuilderPage />)} />
        <Route path="/documents/quote/:docKey" element={requireAccess(<QuoteBuilderPage />)} />
        <Route path="/documents/invoice" element={requireAccess(<InvoiceBuilderPage />)} />
        <Route path="/documents/invoice/:docKey" element={requireAccess(<InvoiceBuilderPage />)} />
        <Route path="/documents/g702" element={requireAccess(<G702BuilderPage />)} />
        <Route path="/documents/g702/:docKey" element={requireAccess(<G702BuilderPage />)} />
        <Route path="/documents" element={requireAccess(<DocumentsPage />, { allowGrace: true })} />
        <Route path="/documents/:docKey" element={requireAccess(<DocumentViewPage />, { allowGrace: true })} />
        <Route path="/clients" element={requireAccess(<ClientsPage />, { allowGrace: true })} />
        <Route path="/clients/new" element={requireAccess(<ClientFormPage />)} />
        <Route path="/clients/:clientId/edit" element={requireAccess(<ClientFormPage />)} />
        <Route path="/clients/:clientId" element={requireAccess(<ClientDetailPage />, { allowGrace: true })} />
        <Route path="/contracts" element={requireAccess(<ContractsPage />, { allowGrace: true })} />
        <Route path="/contracts/new" element={requireAccess(<ContractFormPage />)} />
        <Route path="/contracts/:contractId/edit" element={requireAccess(<ContractFormPage />)} />
        <Route path="/contracts/:contractId" element={requireAccess(<ContractDetailPage />, { allowGrace: true })} />
        <Route path="/catalog" element={requireAccess(<CatalogPage />, { allowGrace: true })} />
        <Route path="/reports" element={requireAccess(<ReportsPage />, { allowGrace: true })} />
        <Route path="/account" element={requireAccess(<AccountPage />, { allowGrace: true })} />
        <Route path="/help" element={requireAccess(<HelpPage />, { allowGrace: true })} />

        {/* Internal ops console (backlog ADMIN-2) — deliberately OUTSIDE
            requireAccess(): a founder shouldn't be paywall-blocked from ops
            tooling by their own trial/subscription state. Still requires a
            real signed-in session; the actual admin-only gate is entirely
            server-side in the admin-stats Edge Function (see AdminPage). Never
            linked from customer nav anywhere in this app. */}
        <Route path="/admin" element={session ? <AdminPage /> : <Navigate to="/login" replace />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
